import { FC, RefObject, useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Button } from '@/atoms/Button'
import { PayItForwardOutlineIcon } from '@/atoms/Icons/PayItForwardOutlineIcon'
import { useTranslate } from '@/utils/translate/translate-client'

interface FloatingPifButtonProps {
  targetRef: RefObject<HTMLElement>
  onShowOffers?: () => void
}

export const FloatingPifButton: FC<FloatingPifButtonProps> = ({ targetRef, onShowOffers }) => {
  const { t } = useTranslate('pif')
  const [isIntersecting, setIsIntersecting] = useState(false)
  const scrollTimeoutRef = useRef<NodeJS.Timeout>()

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const element = targetRef.current
    if (!element) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry) {
          setIsIntersecting(entry.isIntersecting)
        }
      },
      {
        rootMargin: '0px 0px -120px 0px',
        threshold: 0.1,
      },
    )

    observer.observe(element)

    return () => {
      observer.unobserve(element)
    }
  }, [targetRef])

  const handleClick = () => {
    onShowOffers?.()

    scrollTimeoutRef.current = setTimeout(() => {
      targetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)
  }

  return (
    <AnimatePresence>
      {!isIntersecting && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1, transition: { type: 'tween', duration: 0.3 } }}
          exit={{ y: 100, opacity: 0, transition: { type: 'tween', duration: 0.2 } }}
          className="fixed inset-x-0 bottom-0 z-50 p-4 md:hidden"
        >
          <Button
            onClick={handleClick}
            className="ml-auto flex w-fit items-center justify-center gap-2"
            variant="oxide-primary"
            size="sm"
          >
            <PayItForwardOutlineIcon size={20} color="white" />
            {t('payItForward', 'Pay it Forward')}
          </Button>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
